import { useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarCheck } from 'lucide-react';
import BrandLogo from './BrandLogo';
import CourtHorizonArt from './CourtHorizonArt';
import {
  BOOK_COURT_SCREEN_TITLE,
  BOOK_COURT_SCREEN_MESSAGE_LEAD,
  BOOK_COURT_SCREEN_MESSAGE_HIGHLIGHT,
  BOOK_COURT_SCREEN_MESSAGE_TRAIL,
  BOOK_COURT_SCREEN_SUBTEXT,
  BOOK_COURT_SCREEN_CTA_LABEL,
  BOOK_COURT_SCREEN_LOADING_LABEL,
  BOOKING_GRID_URL,
  CONNECTION_ERROR_TITLE,
  CONNECTION_ERROR_MESSAGE,
  CONNECTION_ERROR_RETRY_LABEL,
  CONNECTION_ERROR_REFRESHING_LABEL,
  FACEBOOK_BROWSER_MESSAGE,
  FACEBOOK_BROWSER_TRY_AGAIN_LABEL,
  FACEBOOK_BROWSER_OPEN_LABEL,
} from '../constants/bookingConst';
import { getBuildBranding } from '../utils/buildBrandingUtil';
import { hardRefreshPage } from '../utils/hardRefreshUtil';
import { isFacebookInAppBrowser, openInExternalBrowser } from '../utils/inAppBrowserUtil';
import { isStaffRoute } from '../utils/routeUtil';
import { spinnerRing } from '../utils/bookingUtils';
import '../css/ConnectionErrorScreen.css';

/**
 * Shown in place of a loading spinner once that spinner has run past the
 * configured timeout (see hooks/useLoadingTimeout.js), or when sign-in fails
 * (AuthGuard.js) — the data never arrived and the visitor is owed a way out.
 *
 * Every instance — app boot, sign-in, admin boot, and the in-panel timeouts
 * (slot grid, Open Play, Track Booking, admin tabs) — floats as a card over a
 * dimmed backdrop, like a modal. It has no close button and ignores taps on the backdrop on purpose:
 * it only shows when the app could not load, so there is nothing behind it to
 * go back to — the button is the way out.
 *
 * Guests get the "Book your court" screen: court art, the club's logo and
 * name, a short welcome and one "View Available Slots" button. That button
 * hard-reloads (hardRefreshPage) into BOOKING_GRID_URL, so the fresh load
 * plays the welcome splash and then opens on the time-slot grid.
 *
 * Staff routes (admin, POS) keep the apology copy and a Refresh button that
 * hard-reloads the screen they were on — the public grid is not where a staff
 * member whose dashboard failed wants to go.
 *
 * Colours follow Config -> Theme the way the app pairs them elsewhere: the
 * card is Header Background, text is Text Color, and the button and art are
 * Active Button Color (button text in Header Background). ConfigProvider
 * injects these as --jt-* properties for every route before any load can time
 * out; see ConnectionErrorScreen.css. Logo and club name come from the build
 * (getBuildBranding), because the live config is exactly what may be missing.
 *
 * Inside Facebook's in-app browser it swaps the message for the "open in your
 * browser" one and adds an Open in Browser button — that webview is a common
 * cause of the failure in the first place.
 *
 * @param {object} props
 * @param {() => void} [props.onRetry] - in-place retry that re-runs the
 *   caller's own fetch, for panels where reloading the page would throw away
 *   what the visitor had selected. When omitted, the button hard-reloads
 *   (into the booking grid for guests, in place for staff).
 * @returns {JSX.Element}
 */
function ConnectionErrorScreen({ onRetry }) {
  // UA, route and build branding never change while this screen is up.
  const inFacebook = useMemo(() => isFacebookInAppBrowser(), []);
  const staff = useMemo(() => isStaffRoute(window.location.pathname), []);
  const { clubName, logoUrl } = useMemo(() => getBuildBranding(), []);
  // Set once a hard reload starts; the card swaps to a spinner so repeated
  // taps cannot queue several reloads.
  const [refreshing, setRefreshing] = useState(false);
  const titleId = useId();

  // Several callers sit inside scrollable pages or modals; lock the page
  // behind the overlay so a swipe on the backdrop does not scroll it.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  /**
   * Runs the caller's in-place retry, or hard-reloads — into the booking grid
   * for guests, the current page for staff.
   * @returns {void}
   */
  const handlePrimary = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    setRefreshing(true);
    hardRefreshPage(staff ? undefined : BOOKING_GRID_URL);
  };

  const title = staff ? CONNECTION_ERROR_TITLE : BOOK_COURT_SCREEN_TITLE;
  let primaryLabel = staff ? CONNECTION_ERROR_RETRY_LABEL : BOOK_COURT_SCREEN_CTA_LABEL;
  if (inFacebook) primaryLabel = FACEBOOK_BROWSER_TRY_AGAIN_LABEL;

  let message;
  if (inFacebook) {
    message = FACEBOOK_BROWSER_MESSAGE;
  } else if (staff) {
    message = CONNECTION_ERROR_MESSAGE;
  } else {
    message = (
      <>
        {BOOK_COURT_SCREEN_MESSAGE_LEAD}
        <strong>{BOOK_COURT_SCREEN_MESSAGE_HIGHLIGHT}</strong>
        {BOOK_COURT_SCREEN_MESSAGE_TRAIL}
      </>
    );
  }

  const card = refreshing ? (
    <div className="jt-conn-error-card jt-conn-error-card--busy" role="status" aria-live="polite">
      {spinnerRing(staff ? CONNECTION_ERROR_REFRESHING_LABEL : BOOK_COURT_SCREEN_LOADING_LABEL, logoUrl)}
    </div>
  ) : (
    <div
      className="jt-conn-error-card"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-live="assertive"
    >
      <h2 id={titleId} className="jt-conn-error-title">{title}</h2>
      <CourtHorizonArt className="jt-conn-error-art" />

      <div className="jt-conn-error-brand">
        <BrandLogo src={logoUrl} alt={clubName} className="jt-conn-error-logo" />
        {clubName && <p className="jt-conn-error-club">{clubName}</p>}
      </div>

      <p className="jt-conn-error-message">{message}</p>
      {!staff && !inFacebook && (
        <p className="jt-conn-error-subtext">{BOOK_COURT_SCREEN_SUBTEXT}</p>
      )}

      <div className="jt-conn-error-actions">
        <button type="button" className="jt-conn-error-cta" onClick={handlePrimary}>
          {!staff && <CalendarCheck className="jt-conn-error-cta-icon" aria-hidden="true" />}
          <span>{primaryLabel}</span>
        </button>
        {inFacebook && (
          <button
            type="button"
            className="jt-conn-error-cta jt-conn-error-cta--secondary"
            onClick={openInExternalBrowser}
          >
            <span>{FACEBOOK_BROWSER_OPEN_LABEL}</span>
          </button>
        )}
      </div>
    </div>
  );

  // Portalled to <body> so the overlay floats over the whole viewport even
  // when a caller renders it inside a transformed, clipped or z-indexed panel
  // (the slot grid column, the Open Play modal, admin tabs).
  return createPortal(<div className="jt-conn-error">{card}</div>, document.body);
}

export default ConnectionErrorScreen;
